import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRegisterMutation, useGetUserCategoriesQuery } from "../api/authApi";
import { getDefaultRouteForUser } from "../utils/redirect";

const STORAGE_KEY = "logbook_auth_user_v2";

const COUNTRIES = [
  {
    code: "+234",
    flag: "🇳🇬",
    label: "NG",
    placeholder: "0801 234 5678",
    digitCount: 11,
    format: (d: string) => {
      // 080X XXX XXXX
      const n = d.slice(0, 11);
      if (n.length <= 4) return n;
      if (n.length <= 7) return `${n.slice(0, 4)} ${n.slice(4)}`;
      return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
    },
  },
  {
    code: "+1",
    flag: "🇺🇸",
    label: "US",
    placeholder: "(555) 123-4567",
    digitCount: 10,
    format: (d: string) => {
      // (XXX) XXX-XXXX
      const n = d.slice(0, 10);
      if (n.length <= 3) return n.length ? `(${n}` : "";
      if (n.length <= 6) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
      return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
    },
  },
  {
    code: "+44",
    flag: "🇬🇧",
    label: "UK",
    placeholder: "07911 123 456",
    digitCount: 11,
    format: (d: string) => {
      // XXXXX XXX XXX
      const n = d.slice(0, 11);
      if (n.length <= 5) return n;
      if (n.length <= 8) return `${n.slice(0, 5)} ${n.slice(5)}`;
      return `${n.slice(0, 5)} ${n.slice(5, 8)} ${n.slice(8)}`;
    },
  },
];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const { data: categoriesData } = useGetUserCategoriesQuery();

  const [userCategoryId, setUserCategoryId] = useState("");
  const [countryIdx, setCountryIdx] = useState(0);
  const [phoneDigits, setPhoneDigits] = useState("");
  const [email, setEmail] = useState("");

  const country = COUNTRIES[countryIdx];
  const phone = `${country.code}${phoneDigits}`;
  const [localError, setLocalError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);

    localStorage.removeItem("token");
    localStorage.removeItem(STORAGE_KEY);

    try {
      const res = await register({
        email,
        phone: `${country.code}${phoneDigits}`,
        user_category_id: Number(userCategoryId),
      }).unwrap();

      if (!res?.token || !res?.user) {
        setLocalError(res?.message || res?.error || "Registration failed");
        return;
      }

      localStorage.setItem("token", res.token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(res.user));

      navigate(getDefaultRouteForUser(), { replace: true });
    } catch (err: any) {
      setLocalError(
        err?.data?.message || err?.message || "Registration failed."
      );
    }
  };

  return (
    <div className="fixed inset-0 h-screen w-screen flex overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-900">
      {/* LEFT SIDE */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-10 border-r border-white/5">
        <div className="max-w-md text-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-400/40">
              <span className="text-lg font-bold text-indigo-300">ADM</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">
                NIQS PORTAL
              </div>
              <div className="text-base font-semibold text-slate-100">
                Register
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">Create your account</h1>
            <p className="text-sm text-slate-300">
              Register as a probationer and access your portal.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-100 px-6 py-8 sm:px-8 sm:py-10">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-semibold text-slate-900">
                NIQS PORTAL
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Fill the form to create your account.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Category of Application
                </label>
                <select
                  value={userCategoryId}
                  onChange={(e) => setUserCategoryId(e.target.value)}
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="">Select category</option>
                  {categoriesData?.data
                    ?.filter((cat: any) => ![2, 8, 9, 7, 1, 6].includes(cat.id))
                    ?.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Phone Number
                </label>
                <div className="flex">
                  {/* Country selector */}
                  <select
                    value={countryIdx}
                    onChange={(e) => {
                      setCountryIdx(Number(e.target.value));
                      setPhoneDigits("");
                    }}
                    className="shrink-0 px-2 py-2 text-sm border border-r-0 border-slate-200 rounded-l-md focus:ring-2 focus:ring-indigo-500 outline-none bg-slate-50 text-slate-700 font-medium"
                  >
                    {COUNTRIES.map((c, i) => (
                      <option key={c.code} value={i}>
                        {c.flag} {c.label} {c.code}
                      </option>
                    ))}
                  </select>
                  {/* Phone input */}
                  <input
                    value={country.format(phoneDigits)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, country.digitCount);
                      setPhoneDigits(digits);
                    }}
                    type="tel"
                    required
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-200 rounded-r-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder={country.placeholder}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Email
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="you@example.com"
                />
              </div>

              {localError && (
                <div className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                  {localError}
                </div>
              )}

              <button
                disabled={isLoading}
                type="submit"
                className="w-full py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full py-2.5 text-sm font-medium bg-slate-100 text-slate-800 rounded-md hover:bg-slate-200 transition-colors"
              >
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
